import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, isValid, lightFormat, parseISO } from 'date-fns';
import { isEmpty } from 'lodash';
// @mui
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import {
  BookingIdStatusTimeBar,
  BookingsManagementSkeleton,
  BookingSummary,
  CleaingProducts,
  ClientAddressCleanerTopSection,
  ConfirmRecurringChangesDialog,
  Coupon,
  LinenAmnetities,
  Notes,
  Pets,
  RecurrenceDayChangeDialog,
  ServiceDetails,
  ServiceExtras,
  ValidationErrorsList,
} from '../..';
// API
import { getSettingsData } from '@/features/app/api';
import {
  createBooking,
  getFilteredBookingSession,
  getSingleBooking,
  sendBookingPaymentMethodRequest,
  updateBooking,
} from '@/features/bookings-management/api';
import {
  createUserCustomPrice,
  getUserCustomPrices,
  updateUserCustomPrice,
} from '@/features/users/shared/api';
// redux
import { dispatch, useSelector } from '@/stores';
import {
  updateStoreWithCurrentBooking,
  selectedVariant,
  updateServiceVariantsAndExtras,
  initializeNewBookingStatus,
  setClientCustomPrices,
  buildManualClientCustomPriceDto,
  findMatchingClientCustomPrice,
} from '@/features/bookings-management/slices';
// utils + hooks
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import {
  buildBookingPricingPayload,
  buildBookingPaymentMethodPayload,
  buildBookingEffectiveFromPayload,
  formatRecurringDayIdsForApi,
  getRecurrenceIntervalFromVariant,
  recurrenceDaysChanged,
  showClientCleanerRestrictionWarnings,
  validateBookingForm,
} from '@/features/bookings-management/utils';
import { bookingsManagementPath } from '@/features/bookings-management/routes/paths';
// types
import { ServiceVariant } from '@/types';
import type { BookingRecurrenceChangePayload } from '@/features/bookings-management/api/bookings';
import type { BookingSession } from '@/features/bookings-management/types';
import { UserAddress } from '@/features/users/shared/types';

// ----------------------------------------------------------------------

const toDateValue = (value?: string | Date | null) => {
  if (!value) return '';

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? ''
      : lightFormat(value, 'yyyy-MM-dd');
  }

  return String(value).slice(0, 10);
};

const getSessionTimestamp = (session: BookingSession) =>
  `${toDateValue(session.date)}T${session.time ?? '00:00'}`;

const sortSessionsByDateTime = (sessions: BookingSession[]) =>
  [...sessions].sort((firstSession, secondSession) =>
    getSessionTimestamp(firstSession).localeCompare(
      getSessionTimestamp(secondSession)
    )
  );

const formatDisplayDate = (value?: string | null) => {
  if (!value) return '';
  const parsedDate = parseISO(toDateValue(value));

  return isValid(parsedDate) ? format(parsedDate, 'EEE, d MMM yyyy') : value;
};

const formatSessionLabel = (session: BookingSession) =>
  `#${session.id} - ${formatDisplayDate(session.date)}${
    session.time ? ` at ${session.time}` : ''
  }`;

type BookingDetailsEffectiveChangePayload = Pick<
  BookingRecurrenceChangePayload,
  'effective_from_date' | 'effective_from_session_id'
>;

export default function BookingNewEditForm() {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const { errorMsg, successMsg, warningMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();

  const [formIsSubmitting, setFormIsSubmitting] = useState<boolean>(false);
  const [paymentRequestBookingId, setPaymentRequestBookingId] = useState<
    number | null
  >(null);
  const [paymentRequestIsSending, setPaymentRequestIsSending] = useState(false);
  const [isRecurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [isRecurrenceDayChangeDialogOpen, setRecurrenceDayChangeDialogOpen] =
    useState(false);
  const [effectiveFromDate, setEffectiveFromDate] = useState('');
  const [effectiveFromSessionId, setEffectiveFromSessionId] = useState('');

  const booking = useSelector((state) => state.booking);
  const choosenVariant: ServiceVariant = useSelector(selectedVariant);
  const shouldShowEffectiveFromControls =
    Boolean(bookingId) && booking.isInitiallyRecurring;
  const todayDate = toDateValue(new Date());
  const effectiveFromDateIsPast =
    Boolean(effectiveFromDate) && effectiveFromDate < todayDate;

  const isHolidayRental =
    !isEmpty(booking.selectedServiceVariant) &&
    choosenVariant.ref.includes('holiday');

  const submitBtnRef = useRef<null | HTMLButtonElement>(null);

  // ===============
  //     QUERIES
  // ===============

  // SETTINGS
  const { data: settingsData, isLoading: isSettingsDataLoading } = useQuery({
    // TODO: probably this data should be fetched on app load
    queryKey: ['settings'],
    queryFn: () => getSettingsData(),
    onSuccess: (data) => {
      dispatch(updateServiceVariantsAndExtras(data)); // and maybe this could be stored in a separate slice
      if (!bookingId) {
        dispatch(initializeNewBookingStatus(data));
      }
    },
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching settings data `);
      navigate(bookingsManagementPath('list'));
    },

    refetchOnWindowFocus: false,
  });

  // BOOKINGS
  const { data: currentBooking, isLoading: isBookingLoading } = useQuery({
    queryKey: ['bookings', bookingId || ''],
    queryFn: () => getSingleBooking(Number(bookingId)),
    onSuccess: (bookingFromApi) =>
      dispatch(updateStoreWithCurrentBooking(bookingFromApi)),
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching booking data`);
      navigate(bookingsManagementPath('list'));
    },
    refetchOnWindowFocus: false, // otherwise the booking state in store would get overwritten on windowFocus
    enabled: !!bookingId && !!settingsData, // we need the serviceVariants and serviceExtras to be loaded before we can set the default values ==> !!settingsData
  });

  const { data: upcomingSessions, isLoading: isLoadingUpcomingSessions } =
    useQuery({
      queryKey: ['bookingSessions', bookingId, 'upcoming', 'booking-edit'],
      queryFn: () => getFilteredBookingSession(Number(bookingId), 'upcoming'),
      onError: (error) => {
        errorMsg(error, 'Unable to load upcoming sessions');
      },
      enabled:
        shouldShowEffectiveFromControls && Number.isFinite(Number(bookingId)),
      refetchOnWindowFocus: false,
    });

  const sortedUpcomingSessions = useMemo(
    () => sortSessionsByDateTime(upcomingSessions ?? []),
    [upcomingSessions]
  );

  useEffect(() => {
    if (!shouldShowEffectiveFromControls) {
      setEffectiveFromDate('');
      setEffectiveFromSessionId('');
      return;
    }

    if (effectiveFromDate || sortedUpcomingSessions.length === 0) return;

    const firstUpcomingSession = sortedUpcomingSessions[0];
    setEffectiveFromSessionId(String(firstUpcomingSession.id));
    setEffectiveFromDate(toDateValue(firstUpcomingSession.date));
  }, [
    effectiveFromDate,
    shouldShowEffectiveFromControls,
    sortedUpcomingSessions,
  ]);

  useEffect(() => {
    setEffectiveFromDate('');
    setEffectiveFromSessionId('');
  }, [bookingId]);

  const selectedEffectiveSessionId = Number(effectiveFromSessionId);
  const selectedEffectiveSession = Number.isFinite(selectedEffectiveSessionId)
    ? sortedUpcomingSessions.find(
        (session) => session.id === selectedEffectiveSessionId
      )
    : undefined;
  const effectiveFromSessionOptions = useMemo(
    () =>
      sortedUpcomingSessions.map((session) => ({
        id: session.id,
        label: formatSessionLabel(session),
      })),
    [sortedUpcomingSessions]
  );

  const handleChangeEffectiveFromSession = (value: string) => {
    setEffectiveFromSessionId(value);

    const nextSessionId = Number(value);
    const nextSession = sortedUpcomingSessions.find(
      (session) => session.id === nextSessionId
    );
    if (nextSession) {
      setEffectiveFromDate(toDateValue(nextSession.date));
    }
  };

  const handleChangeEffectiveFromDate = (value: string) => {
    setEffectiveFromDate(value);
    setEffectiveFromSessionId('');
  };

  useQuery({
    queryKey: ['clientCustomPrices', booking.client?.id],
    queryFn: () => getUserCustomPrices(String(booking.client?.id)),
    onSuccess: (customPrices) => dispatch(setClientCustomPrices(customPrices)),
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching client prices`);
    },
    enabled: !bookingId && !!booking.client?.id,
    refetchOnWindowFocus: false,
  });

  // ===================
  //        SUBMIT
  // ===================

  const saveManualClientCustomPrice = async () => {
    if (
      bookingId ||
      !booking.cleanDetails.manualBaseRateTouched ||
      booking.cleanDetails.manualBaseRate === null ||
      !booking.client?.id ||
      isEmpty(booking.address) ||
      isEmpty(booking.selectedServiceVariant) ||
      !booking.date
    ) {
      return;
    }

    const clientId = String(booking.client.id);
    const cityId = (booking.address as UserAddress).city_id;
    const serviceVariantId = (booking.selectedServiceVariant as ServiceVariant)
      .id;
    const manualPriceDto = buildManualClientCustomPriceDto({
      cityId,
      serviceVariantId,
      price: booking.cleanDetails.manualBaseRate,
      startDate: booking.date,
    });
    const clientCustomPrices =
      booking.custom_user_prices ?? (await getUserCustomPrices(clientId));
    const matchingCustomPrice = findMatchingClientCustomPrice(
      clientCustomPrices,
      serviceVariantId,
      cityId,
      (booking.address as UserAddress).town_or_city,
      booking.date
    );

    if (matchingCustomPrice) {
      await updateUserCustomPrice(
        clientId,
        String(matchingCustomPrice.id),
        manualPriceDto
      );
    } else {
      await createUserCustomPrice(clientId, manualPriceDto);
    }

    queryClient.invalidateQueries(['clientCustomPrices', booking.client.id]);
    queryClient.invalidateQueries(['clients']);
  };

  const resolveCreatedBookingId = (createdBooking: any) => {
    const candidates = [
      createdBooking?.id,
      createdBooking?.data?.id,
      createdBooking?.data?.[0]?.id,
      createdBooking?.booking?.id,
    ];
    const createdBookingId = candidates.find((candidate) =>
      Number.isFinite(Number(candidate))
    );

    return createdBookingId ? Number(createdBookingId) : null;
  };

  const navigateToBookingsList = () => {
    navigate(bookingsManagementPath('list'));
  };

  const invalidateBookingEditQueries = () => {
    queryClient.invalidateQueries(['bookings']);
    queryClient.invalidateQueries(['bookingSessions']);
  };

  const handleClosePaymentRequestDialog = () => {
    setPaymentRequestBookingId(null);
    navigateToBookingsList();
  };

  const handleSendPaymentMethodRequest = async () => {
    if (!paymentRequestBookingId) return;

    try {
      setPaymentRequestIsSending(true);
      await sendBookingPaymentMethodRequest(paymentRequestBookingId);
      successMsg('Payment method email sent');
      handleClosePaymentRequestDialog();
    } catch (error) {
      errorMsg(
        error,
        'Booking was created, but the payment method email could not be sent yet'
      );
    } finally {
      setPaymentRequestIsSending(false);
    }
  };

  const buildFormattedBookingData = () => {
    const recurringDays = formatRecurringDayIdsForApi(booking.recurring_days);
    const effectiveFromPayload =
      shouldShowEffectiveFromControls && effectiveFromDate
        ? buildBookingEffectiveFromPayload({
            effectiveFromDate,
            effectiveFromSessionId: selectedEffectiveSession?.id,
          })
        : {};

    return {
      user_id: booking.client?.id,
      address_id: (booking.address as UserAddress).id,
      booking_cleaners:
        booking.booking_cleaners.length > 0
          ? [booking.booking_cleaners[0].id]
          : [],
      //
      booking_status_id: booking.booking_status_id,
      date: lightFormat(new Date(booking.date as Date), 'yyyy-MM-dd'),
      time: booking.time,
      //
      service_variant_id: (booking.selectedServiceVariant as ServiceVariant).id,
      bedrooms: booking.cleanDetails.numberOfBedrooms,
      bathrooms: booking.cleanDetails.numberOfBathrooms,
      desks: booking.cleanDetails.numberOfDesks,
      hours: booking.cleanDetails.hours,
      extras: booking.selectedServiceCleaningExtras
        .map((extra) => extra.id)
        .join('|'),
      //
      provider_instructions: booking.cleanDetails.notes.provider_instructions,
      access_instructions: booking.cleanDetails.access_instructions_enabled
        ? booking.cleanDetails.notes.access_instructions
        : '',
      access_type: booking.cleanDetails.access_instructions_enabled
        ? booking.cleanDetails.access_type
        : '',
      admin_notes: booking.cleanDetails.notes.admin_notes,
      total_amount: booking.totalAmount,
      ...buildBookingPricingPayload(booking.cleanDetails),
      ...buildBookingPaymentMethodPayload(booking),
      //
      cleaning_products: booking.cleanDetails.requestForCleaningProduct,
      pets: booking.cleanDetails.pets.join('|'),
      linen: booking.cleanDetails.linen,
      essential_amenities: booking.cleanDetails.essential_amenities,
      property_type_id: booking.cleanDetails.property_type_id,
      // coupon
      coupon_code_id: Number(booking.cleanDetails.couponObject?.id),
      recurring_days: recurringDays,
      ...effectiveFromPayload,
      // cleaning_sessions_date:
    };
  };

  const recurringDaysForApi = formatRecurringDayIdsForApi(
    booking.recurring_days
  );
  const previousRecurringDays = booking.booking_recurrence?.week_days ?? '';
  const targetIntervalNumber = getRecurrenceIntervalFromVariant(
    booking.selectedServiceVariant as ServiceVariant
  );
  const previousIntervalNumber =
    booking.booking_recurrence?.interval_number ?? null;
  const hasRecurrenceIntervalChange =
    Boolean(bookingId) &&
    booking.isInitiallyRecurring &&
    Boolean(targetIntervalNumber) &&
    Boolean(previousIntervalNumber) &&
    targetIntervalNumber !== previousIntervalNumber;
  const hasRecurrenceDayChange =
    Boolean(bookingId) &&
    booking.isInitiallyRecurring &&
    recurrenceDaysChanged(previousRecurringDays, recurringDaysForApi);
  const hasRecurrenceChange =
    hasRecurrenceDayChange || hasRecurrenceIntervalChange;

  const saveExistingBookingDetailsWithoutRecurrenceDays = async (
    effectiveChangePayload?: BookingDetailsEffectiveChangePayload
  ) => {
    if (!bookingId) return;

    const {
      recurring_days: _recurringDays,
      effective_from_date: _effectiveFromDate,
      effective_from_session_id: _effectiveFromSessionId,
      first_affected_session_id: _firstAffectedSessionId,
      ...bookingDetails
    } = buildFormattedBookingData();

    await updateBooking(Number(bookingId), {
      ...bookingDetails,
      ...effectiveChangePayload,
    });
    queryClient.invalidateQueries(['bookings']);
  };

  const handleSubmitBooking = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isFormValid = validateBookingForm(dispatch, booking, {
      requirePaymentMethod: !bookingId,
    });

    if (!isFormValid) return;
    if (shouldShowEffectiveFromControls && effectiveFromDateIsPast) {
      errorMsg('Choose today or a future date for affected sessions');
      return;
    }

    const shouldPromptForPaymentMethodRequest =
      !bookingId &&
      Boolean(booking.client?.id) &&
      (booking.client?.wallet?.length ?? 0) === 0;

    const formattedData = buildFormattedBookingData();

    try {
      setFormIsSubmitting(true);
      if (bookingId) {
        const updatedBooking = await updateBooking(
          Number(bookingId),
          formattedData
        );
        setRecurringDialogOpen(false);
        successMsg(`Booking #${bookingId} updated successfully`);
        showClientCleanerRestrictionWarnings(
          updatedBooking,
          warningMsg,
          booking.booking_cleaners
        );
        invalidateBookingEditQueries();
        return;
      }

      const createdBooking = await createBooking(formattedData as any);
      await saveManualClientCustomPrice();
      successMsg('Booking created successfully');
      showClientCleanerRestrictionWarnings(
        createdBooking,
        warningMsg,
        booking.booking_cleaners
      );
      if (shouldPromptForPaymentMethodRequest) {
        const createdBookingId = resolveCreatedBookingId(createdBooking);
        if (!createdBookingId) {
          errorMsg(
            'Booking was created, but the payment method request could not be prepared yet'
          );
          navigateToBookingsList();
          queryClient.invalidateQueries(['bookings']);
          return;
        }
        setPaymentRequestBookingId(createdBookingId);
        queryClient.invalidateQueries(['bookings']);
        return;
      }
      navigateToBookingsList();
      queryClient.invalidateQueries(['bookings']);
    } catch (error) {
      errorMsg(
        error,
        `Something went wrong while ${
          bookingId ? 'updating' : 'creating'
        } booking`
      );
    } finally {
      setFormIsSubmitting(false);
    }
  };

  const initiateSubmit = () => {
    submitBtnRef.current?.click();
  };

  const handleClickUpdateBooking = () => {
    const isFormValid = validateBookingForm(dispatch, booking, {
      requirePaymentMethod: false,
    });

    if (!isFormValid) return;

    if (hasRecurrenceChange) {
      setRecurrenceDayChangeDialogOpen(true);
      return;
    }

    setRecurringDialogOpen(true);
  };

  const handleRecurrenceDayChangeApplied = () => {
    setRecurrenceDayChangeDialogOpen(false);
    invalidateBookingEditQueries();
  };

  const validationErrors = Object.values(booking.validationErrors).filter(
    (error) => typeof error === 'string'
  );

  // ===================

  const showSkeleton =
    isSettingsDataLoading ||
    (bookingId && isBookingLoading) ||
    (bookingId && !currentBooking) ||
    booking.bookingLoading ||
    (bookingId && isEmpty(choosenVariant));

  // ===================

  return (
    <form onSubmit={(e) => handleSubmitBooking(e)}>
      <Card sx={{ pb: 4 }}>
        {/* SKELETON */}
        {showSkeleton ? (
          <BookingsManagementSkeleton />
        ) : (
          // TOP SECTION
          <Stack direction="column" spacing={0.1}>
            <ClientAddressCleanerTopSection />
            <BookingIdStatusTimeBar
              bookingStatuses={settingsData?.bookingStatuses || []}
            />

            {/* MAIN SECTION */}
            <Grid container sx={{ pl: 2, pr: 4 }} spacing={2}>
              {/* LEFT */}
              <Grid
                item
                container
                xs={12}
                md={8}
                spacing={2}
                sx={{ display: 'block' }}
              >
                <Grid item container spacing={6}>
                  <Grid item xs={12} md={7}>
                    <ServiceDetails />
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <ServiceExtras />

                    {isHolidayRental && (
                      <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
                        <Divider sx={{ width: '90%', pb: 1 }} />
                        <LinenAmnetities />
                      </Stack>
                    )}
                  </Grid>
                </Grid>

                <Grid item container xs={12} sx={{ mt: 4 }}>
                  <Notes />
                </Grid>

                <Grid item container xs={12} sx={{ mt: 4 }}>
                  <Pets />
                </Grid>
              </Grid>

              {/* RIGHT */}
              <Grid
                item
                container
                xs={12}
                md={4}
                direction="column"
                spacing={2}
                sx={{ mt: 2 }}
              >
                <BookingSummary />
                <Grid item>
                  <CleaingProducts />
                </Grid>
                <Grid item>
                  <Coupon />
                </Grid>

                <Grid
                  item
                  container
                  justifyContent="flex-end"
                  direction="row"
                  spacing={2}
                  sx={{ mt: 'auto' }}
                >
                  <Grid item>
                    {!bookingId && (
                      <LoadingButton
                        type="submit"
                        size="large"
                        variant="contained"
                        loading={formIsSubmitting}
                      >
                        Create booking
                      </LoadingButton>
                    )}

                    <LoadingButton
                      type="submit"
                      size="large"
                      variant="contained"
                      ref={submitBtnRef}
                      loading={formIsSubmitting}
                      sx={{ visibility: 'hidden', display: 'none' }}
                    >
                      Update booking
                    </LoadingButton>

                    {bookingId && (
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleClickUpdateBooking}
                      >
                        Update booking
                      </Button>
                    )}
                    {!!validationErrors.length && (
                      <ValidationErrorsList
                        validationErrors={validationErrors}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Stack>
        )}
      </Card>

      <ConfirmRecurringChangesDialog
        isDialogOpen={isRecurringDialogOpen}
        handleCloseDialog={() => setRecurringDialogOpen(false)}
        handleConfirmDialog={() => initiateSubmit()}
        isSubmitting={formIsSubmitting}
        affectedSessions={
          shouldShowEffectiveFromControls
            ? {
                date: effectiveFromDate,
                dateIsPast: effectiveFromDateIsPast,
                isLoading: isLoadingUpcomingSessions,
                sessionId: effectiveFromSessionId,
                sessionOptions: effectiveFromSessionOptions,
                todayDate,
                onChangeDate: handleChangeEffectiveFromDate,
                onChangeSession: handleChangeEffectiveFromSession,
              }
            : undefined
        }
      />

      {bookingId && (
        <RecurrenceDayChangeDialog
          bookingId={Number(bookingId)}
          isDialogOpen={isRecurrenceDayChangeDialogOpen}
          recurringDays={recurringDaysForApi}
          previousRecurringDays={previousRecurringDays}
          intervalNumber={targetIntervalNumber}
          previousIntervalNumber={previousIntervalNumber}
          onCloseDialog={() => setRecurrenceDayChangeDialogOpen(false)}
          onApplied={handleRecurrenceDayChangeApplied}
          onSaveBookingDetails={saveExistingBookingDetailsWithoutRecurrenceDays}
        />
      )}

      <Dialog
        fullWidth
        maxWidth="xs"
        open={paymentRequestBookingId !== null}
        onClose={
          paymentRequestIsSending ? undefined : handleClosePaymentRequestDialog
        }
      >
        <DialogTitle>Send payment method request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This booking was created without a saved card. Send the client an
            email with a link to add a payment method.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePaymentRequestDialog}
            disabled={paymentRequestIsSending}
          >
            Skip for now
          </Button>
          <LoadingButton
            variant="contained"
            loading={paymentRequestIsSending}
            disabled={!paymentRequestBookingId}
            onClick={handleSendPaymentMethodRequest}
          >
            Send payment method email
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </form>
  );
}
